import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ThemePrefs } from "@shared/types";

type Palette = ThemePrefs["palette"];
type Mode = ThemePrefs["mode"];

interface ThemeCtx {
  palette: Palette;
  mode: Mode;
  resolvedMode: "light" | "dark";
  setPalette: (p: Palette) => void;
  setMode: (m: Mode) => void;
}

const Ctx = createContext<ThemeCtx | null>(null);
const KEY = "longevity.theme";

const PALETTES: { id: Palette; label: string; swatch: string }[] = [
  { id: "longevity", label: "Longevity Green", swatch: "#1f3d2b" },
  { id: "teal", label: "Clinical Teal", swatch: "#0f766e" },
  { id: "slate", label: "Slate", swatch: "#37485f" },
  { id: "gold", label: "Bronze", swatch: "#a67c3a" },
];

function read(): { palette: Palette; mode: Mode } {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "{}");
    return { palette: v.palette || "longevity", mode: v.mode || "system" };
  } catch {
    return { palette: "longevity", mode: "system" };
  }
}

export function ThemeProvider({ children, initial }: { children: React.ReactNode; initial?: ThemePrefs | null }) {
  const [palette, setPaletteState] = useState<Palette>(initial?.palette || read().palette);
  const [mode, setModeState] = useState<Mode>(initial?.mode || read().mode);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const resolvedMode: "light" | "dark" = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", palette);
    root.setAttribute("data-theme-mode", resolvedMode);
    root.classList.toggle("dark", resolvedMode === "dark");
    localStorage.setItem(KEY, JSON.stringify({ palette, mode }));
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", resolvedMode === "dark" ? "#0f1411" : "#1f3d2b");
  }, [palette, mode, resolvedMode]);

  const setPalette = useCallback((p: Palette) => setPaletteState(p), []);
  const setMode = useCallback((m: Mode) => setModeState(m), []);

  const value = useMemo(
    () => ({ palette, mode, resolvedMode, setPalette, setMode }),
    [palette, mode, resolvedMode, setPalette, setMode],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export { PALETTES };
